import React, { useState } from 'react';

import { HistoryTransforms, Select, variableListRowHeader } from '@aline/common';
import { params } from '@aline/routes';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import type { PanelVariableType } from 'src/components';
import { AlineAvatar, AlineTooltip, Spacer, useIsFieldInDocJSON } from 'src/components';
import { CompletedIcon } from 'src/components/icons/CompletedIcon';
import { HorizontalDotsIcon } from 'src/components/icons/HorizontalDotsIcon';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { ClientTransforms } from 'src/editor/transforms';
import { Apollo } from 'src/gql/generated';
import { useDispatchUndoSnack } from 'src/helpers';
import {
  useDeleteField,
  useDocumentSignatureStatus,
  useIsAuthorizedToEditStorable,
} from 'src/hooks';
import { CrmIntegrationIconWithTooltip } from 'src/images/integrations';
import { useMaybeEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  completedIcon: {
    color: theme.appColors.accent2,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  icon: {
    height: 18,
    width: 18,
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
  },
  variableId: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

interface VariableRowHeaderProps {
  documentId: UUID;
  isCompleted: boolean;
  variable: PanelVariableType;
}

export function VariableRowHeader({
  documentId,
  isCompleted,
  variable,
}: VariableRowHeaderProps): JSX.Element {
  const { classes } = useStyles();
  const editor = useMaybeEditor();
  const documentViewer = useMaybeDocumentViewer();
  const dispatchUndoSnack = useDispatchUndoSnack();
  const [open, setOpen] = useState<boolean>(false);
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { isInChildren } = useIsFieldInDocJSON(variable);
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const { deleteField } = useDeleteField(editor, documentViewer, tabId);
  const { setValue: setEditFieldId } = useDrawerSearchParam(params.rightPanel.editFieldId);
  const { isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isInOrAfterSigning = wasSentForSignature || isSigned;
  const isFieldInOrAfterSigning = variable.isField && isInOrAfterSigning;
  // Can't delete a field from the doc if you're not Editor, or it's during or after signing.
  const isDeleteDisabled = !canEdit || isFieldInOrAfterSigning;

  async function handleDeleteVariable(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    setOpen(false);

    const { data } = await deleteField(variable);
    if (editor && isInChildren) {
      ClientTransforms.focus(editor);
      if (data)
        dispatchUndoSnack('Deleted field in document.', () => HistoryTransforms.undo(editor));
    }
  }

  function handleEditVariable(e: React.MouseEvent): void {
    e.stopPropagation();
    setEditFieldId(variable.id);
  }

  return (
    <div className={classes.header} data-cy={variableListRowHeader}>
      <Typography noWrap className={classes.variableId}>
        {variable.label}
        {variable.variant === Apollo.MetadataVariant.Checkbox &&
          getCheckboxSelectionLabelSuffix(variable.minRequired, variable.maxRequired)}
      </Typography>
      <Spacer />
      {variable.integrationLink?.integration && variable.integrationLink?.attributePath && (
        <CrmIntegrationIconWithTooltip
          attributePath={variable.integrationLink.attributePath}
          integration={variable.integrationLink.integration}
        />
      )}
      {isCompleted && <CompletedIcon className={classes.completedIcon} />}
      <VariableIcon variable={variable} />
      <IconButton
        ref={setAnchorEl}
        aria-label="Variable menu"
        data-cy={Select.document.editor.rightDrawer.outline.variableRow.editButton}
        size="small"
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <HorizontalDotsIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        <MenuItem onClick={handleEditVariable}>
          <Typography
            data-cy={Select.document.editor.rightDrawer.outline.variableRow.editMenuItem}
            variant="body1"
          >
            Edit
          </Typography>
        </MenuItem>
        <MenuItem disabled={isDeleteDisabled} onClick={handleDeleteVariable}>
          <Typography
            data-cy={Select.document.editor.rightDrawer.outline.variableRow.deleteMenuItem}
            variant="body1"
          >
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </div>
  );
}

interface VariableIconProps {
  variable: PanelVariableType;
}

function VariableIcon({ variable }: VariableIconProps): JSX.Element {
  function getTooltipMsg(): string {
    const tooltipStringParts = [
      variable.action?.assignee?.displayName ??
        variable.action?.assigneeRole?.roleName ??
        'Unassigned',
    ];
    const isAssignedRole =
      Boolean(variable.action?.assignee) && Boolean(variable.action?.assigneeRole);
    if (isAssignedRole) tooltipStringParts.push(`(${variable.action?.assigneeRole?.roleName})`);

    return tooltipStringParts.join(' ');
  }

  return (
    <AlineTooltip arrow placement="top" title={getTooltipMsg()}>
      <AlineAvatar avatarInfo={variable.action?.assignee ?? variable.action?.assigneeRole} />
    </AlineTooltip>
  );
}

function getCheckboxSelectionLabelSuffix(
  minRequired: number = 0,
  maxRequired: number | null = null
): string {
  if (minRequired === 0 && maxRequired === null) return '';
  if (minRequired > 0 && maxRequired === null) return ` (Select at least ${minRequired})`;
  if (minRequired === 0 && maxRequired !== null) return ` (Select up to ${maxRequired})`;
  return ` (Select ${minRequired}–${maxRequired})`;
}
